/**
 * The studio's mapping table (D32): judged against a show style the user
 * picks (the studio's default first), one row per show-style layer — the
 * drivers it needs, the piece types using it, and the studio's mapping of it
 * (device, lookahead, one-shot, options), editable. A layer a piece type uses
 * that is unmapped, or mapped to a device of a driver it does not allow, is
 * flagged; the pair's problems (as `acp validate` and a production create
 * would say them) are listed below. Rows come from `lib/studio/mappings.ts`;
 * every edit is an event the view turns into the studio document.
 */
import { useState } from 'react';
import { type ConfigProblem, isJsonObject } from '../../lib/studio/config.js';
import { type MappingEdit, type MappingRow, newMapping } from '../../lib/studio/mappings.js';
import { type ShowStyleChoice, ShowStylePicker } from '../showStyles/ShowStylePicker.js';
import { Badge } from '../ui/badge.js';
import { Input } from '../ui/input.js';
import { NativeSelect } from '../ui/native-select.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';
import { ConfigProblemList } from './ConfigProblemList.js';

export type StudioMappingTableProps = {
  readonly showStyles: readonly ShowStyleChoice[];
  /** The show style judged against ('' for none). */
  readonly showStyleId: string;
  readonly studioDefault: string | null;
  /** The rows, or null while there are none to show (`notice` says why). */
  readonly rows: readonly MappingRow[] | null;
  readonly notice: string | null;
  /** The pair's problems (paths `showStyle.…` / `studio.…`). */
  readonly problems: readonly ConfigProblem[];
  readonly canEdit: boolean;
  readonly onShowStyle: (showStyleId: string) => void;
  /** Map `layer` as `edit`, or unmap it (null). */
  readonly onMap: (layer: string, edit: MappingEdit | null) => void;
};

const LOOKAHEAD = ['none', 'preload', 'when-clear'] as const;

function lookaheadOf(value: string): MappingEdit['lookahead'] {
  return LOOKAHEAD.find((l) => l === value) ?? 'none';
}

/** The options as JSON, committed on blur when they parse to an object. */
function OptionsCell({ layer, mapping, disabled, onCommit }: { readonly layer: string; readonly mapping: MappingEdit; readonly disabled: boolean; readonly onCommit: (options: MappingEdit['options']) => void }) {
  const written = JSON.stringify(mapping.options);
  const [text, setText] = useState(written);
  let valid = true;
  try {
    valid = isJsonObject(JSON.parse(text));
  } catch {
    valid = false;
  }
  return (
    <Input
      aria-label={`${layer} options`}
      className="font-mono text-xs"
      value={text}
      disabled={disabled}
      aria-invalid={!valid}
      data-testid="mapping-options"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (!valid || text === written) return;
        const parsed: unknown = JSON.parse(text);
        if (isJsonObject(parsed)) onCommit(parsed);
      }}
    />
  );
}

function MappingTableRow({ row, canEdit, onMap }: { readonly row: MappingRow; readonly canEdit: boolean; readonly onMap: StudioMappingTableProps['onMap'] }) {
  const mapping = row.mapping;
  const mappedElsewhere = mapping !== null && !row.candidates.some((c) => c.id === mapping.device);
  return (
    <TableRow data-testid="mapping-row" data-layer={row.layer} data-problem={row.problem === null ? undefined : 'true'}>
      <TableCell>
        <span className="font-mono">{row.layer}</span>
        {row.label === '' ? null : <span className="block text-xs text-muted-foreground">{row.label}</span>}
      </TableCell>
      <TableCell className="text-xs">{row.deviceTypes.length === 0 ? 'any' : row.deviceTypes.join(', ')}</TableCell>
      <TableCell className="text-xs">{row.usedBy.length === 0 ? '–' : row.usedBy.join(', ')}</TableCell>
      <TableCell>
        <NativeSelect
          aria-label={`${row.layer} device`}
          value={mapping?.device ?? ''}
          disabled={!canEdit}
          data-testid="mapping-device"
          onChange={(e) => {
            const device = e.target.value;
            if (device === '') {
              onMap(row.layer, null);
              return;
            }
            const base = mapping ?? newMapping(row);
            if (base !== null) onMap(row.layer, { ...base, device });
          }}
        >
          <option value="">(unmapped)</option>
          {mappedElsewhere ? <option value={mapping.device}>{mapping.device} (not allowed)</option> : null}
          {row.candidates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.id} ({c.type})
            </option>
          ))}
        </NativeSelect>
      </TableCell>
      <TableCell>
        {mapping === null ? null : (
          <NativeSelect aria-label={`${row.layer} lookahead`} value={mapping.lookahead} disabled={!canEdit} onChange={(e) => onMap(row.layer, { ...mapping, lookahead: lookaheadOf(e.target.value) })}>
            {LOOKAHEAD.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </NativeSelect>
        )}
      </TableCell>
      <TableCell>
        {mapping === null ? null : <input type="checkbox" aria-label={`${row.layer} one-shot`} checked={mapping.oneShot} disabled={!canEdit} onChange={(e) => onMap(row.layer, { ...mapping, oneShot: e.target.checked })} />}
      </TableCell>
      <TableCell className="min-w-48">
        {mapping === null ? null : <OptionsCell key={JSON.stringify(mapping.options)} layer={row.layer} mapping={mapping} disabled={!canEdit} onCommit={(options) => onMap(row.layer, { ...mapping, options })} />}
      </TableCell>
      <TableCell className="text-xs">
        {row.problem !== null ? (
          <span className="text-destructive" data-testid="mapping-problem">
            {row.problem}
          </span>
        ) : mapping === null ? (
          <Badge variant="standby">unused</Badge>
        ) : (
          <Badge variant="success">ok</Badge>
        )}
      </TableCell>
    </TableRow>
  );
}

export function StudioMappingTable(props: StudioMappingTableProps) {
  const { rows } = props;
  return (
    <div className="grid gap-4" data-testid="studio-mapping-table">
      <ShowStylePicker
        id="mapping-show-style"
        label="Show style"
        showStyles={props.showStyles}
        value={props.showStyleId}
        studioDefault={props.studioDefault}
        allowNone={false}
        disabled={false}
        description="The mappings below are judged against this show style’s layers."
        onChange={props.onShowStyle}
      />
      {rows === null ? (
        <p className="text-sm text-muted-foreground" data-testid="mapping-notice">
          {props.notice ?? 'Pick a show style.'}
        </p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Layer</TableHead>
                <TableHead>Needs</TableHead>
                <TableHead>Used by</TableHead>
                <TableHead>Device</TableHead>
                <TableHead>Lookahead</TableHead>
                <TableHead>One-shot</TableHead>
                <TableHead>Options</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <MappingTableRow key={row.layer} row={row} canEdit={props.canEdit} onMap={props.onMap} />
              ))}
            </TableBody>
          </Table>
          {props.problems.length === 0 ? (
            <p className="text-sm" data-testid="mapping-valid">
              <span className="rounded bg-success px-1.5 py-0.5 text-success-foreground">Valid pair</span>{' '}
              <span className="text-muted-foreground">— a production of this show style in this studio would resolve.</span>
            </p>
          ) : (
            <ConfigProblemList problems={props.problems} source="editor" />
          )}
        </>
      )}
    </div>
  );
}
